import { type InputHTMLAttributes, forwardRef, useId } from 'react';

import styles from './CrayonInput.module.css';

export type CrayonInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /** 입력 위에 표시되는 라벨. 손글씨 디스플레이 폰트로 렌더된다. */
  label: string;
  /** 라벨 아래 보조 설명. */
  helperText?: string;
  /** 에러 상태 — 빨간 보더 + 흔들림 애니메이션. `helperText`도 빨갛게 표시된다. */
  error?: boolean;
  /** 에러일 때 보조 메시지를 덮어쓸 수 있는 옵션. */
  errorText?: string;
};

/**
 * 코드 입력 전용 Hero 컴포넌트.
 *
 * - 포커스 시 파란 잉크 그림자로 즉각적인 시각 피드백을 준다.
 * - 에러 상태에선 빨간 보더 + 6px 좌우 진동(`shake` 키프레임).
 * - `text-transform: uppercase`로 입력값을 자동 대문자화 (코드 형식: `KIDS-26-XXXX`).
 *
 * @see darakcast_kidsday_design_system.html — `.code-input` 컴포넌트
 */
export const CrayonInput = forwardRef<HTMLInputElement, CrayonInputProps>(function CrayonInput(
  { label, helperText, error = false, errorText, id, className, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = `${inputId}-helper`;

  const inputClasses = [styles.input, error ? styles.error : null, className]
    .filter(Boolean)
    .join(' ');

  const message = error && errorText ? errorText : helperText;
  const messageClasses = [styles.helper, error ? styles.helperError : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.wrap}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={inputClasses}
        aria-invalid={error}
        aria-describedby={message ? helperId : undefined}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        {...rest}
      />
      {message ? (
        <p id={helperId} className={messageClasses}>
          {message}
        </p>
      ) : null}
    </div>
  );
});
